import { MasterPatternContraction } from '@server/data-feed/schemas/output.schema';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Rectangle } from '../primitives/rectangle.primitive';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import { timeToTimestamp } from '../services/timeframe-utils';
import {
  Time,
  UTCTimestamp,
  ISeriesPrimitive,
  LineSeries,
  ISeriesApi,
  LineStyle
} from 'lightweight-charts';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import {
  isMasterPatternIndicatorUpdate,
  MasterPatternIndicatorUpdate
} from '../services/indicator-update.service';
import { IndicatorType } from '@shared/types';
import {
  CandlestickUpdatePoint,
  NewCandlestickEvent
} from '../services/candlestick-update.service';

const showZigZag = false;

const COLOR_PRESETS = {
  major: {
    fill: 'rgba(248, 76, 76, 0.2)',
    border: 'rgb(248, 76, 76)'
  },
  minor: {
    fill: 'rgba(76, 145, 248, 0.2)',
    border: 'rgb(76, 145, 248)'
  }
} as const;

type MasterPatternPrimitiveMap = Map<number, ISeriesPrimitive<Time>[]>;
type MasterPatternLineSeriesMap = Map<number, ISeriesApi<'Line'>[]>;
type MasterPatternZigZagSeriesMap = Map<number, ISeriesApi<'Line'>>;

export const useMasterPattern = () => {
  const {
    chartRef,
    candlestickSeriesRef,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();
  const { timeframeIndex, newCandlestick$, getLatestCandlesticks } =
    useChartStreamContext();

  // 1) Rendered objects are cached per indicatorIndex so each configured MASTER_PATTERN
  // can be updated/cleared independently without touching artifacts of other instances.
  const masterPatternRefs = useRef<MasterPatternPrimitiveMap>(new Map());
  const zigZagSeriesRefs = useRef<MasterPatternZigZagSeriesMap>(new Map());
  const valuePointSeriesRefs = useRef<MasterPatternLineSeriesMap>(new Map());
  // 2) Latest indicator payload is cached because candle-only updates can still change
  // how open-ended ranges should be drawn (they depend on the latest candle time).
  const indicatorDataCacheRef = useRef<
    Map<number, MasterPatternIndicatorUpdate['data']>
  >(new Map());

  const hasMasterPattern = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.MASTER_PATTERN
      ),
    [indicatorConfigs.overlay]
  );

  const clearPrimitivesForIndicator = useCallback((indicatorIndex: number) => {
    const primitives = masterPatternRefs.current.get(indicatorIndex);
    if (!primitives?.length) return;

    primitives.forEach((primitive) => {
      candlestickSeriesRef.current?.detachPrimitive(primitive);
    });

    masterPatternRefs.current.delete(indicatorIndex);
  }, []);

  const clearValuePointSeriesForIndicator = useCallback(
    (indicatorIndex: number) => {
      const seriesArray = valuePointSeriesRefs.current.get(indicatorIndex);
      if (!seriesArray?.length) return;

      seriesArray.forEach((series) => {
        chartRef.current?.removeSeries(series);
      });

      valuePointSeriesRefs.current.delete(indicatorIndex);
    },
    []
  );

  const clearZigZagSeriesForIndicator = useCallback(
    (indicatorIndex: number) => {
      const series = zigZagSeriesRefs.current.get(indicatorIndex);
      if (!series) return;

      chartRef.current?.removeSeries(series);
      zigZagSeriesRefs.current.delete(indicatorIndex);
    },
    []
  );

  const clearAll = useCallback(() => {
    Array.from(masterPatternRefs.current.keys()).forEach(
      clearPrimitivesForIndicator
    );
    Array.from(valuePointSeriesRefs.current.keys()).forEach(
      clearValuePointSeriesForIndicator
    );
    Array.from(zigZagSeriesRefs.current.keys()).forEach(
      clearZigZagSeriesForIndicator
    );
  }, [
    clearPrimitivesForIndicator,
    clearValuePointSeriesForIndicator,
    clearZigZagSeriesForIndicator
  ]);

  const getUniqueContractionBoxes = useCallback(
    (data: MasterPatternIndicatorUpdate['data']) => {
      const uniqueByRange = new Map<
        string,
        NonNullable<
          MasterPatternIndicatorUpdate['data'][number]['contractions']
        >[number]
      >();

      data
        .flatMap((item) => item.contractions ?? [])
        .forEach((item) => {
          if (!item.contraction_box_start) return;

          const key = `${item.contraction_box_start}-${item.contraction_box_end ?? ''}`;
          // Keep the latest payload per box range. Later payloads can add/update
          // value-point continuation/expiry fields required for extension rendering.
          uniqueByRange.set(key, item);
        });

      return Array.from(uniqueByRange.values());
    },
    []
  );

  const createRectanglePrimitive = useCallback(
    (
      item: MasterPatternContraction,
      lastCandleTime: Time
    ): Rectangle | null => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;

      if (
        !chart ||
        !series ||
        item.contraction_box_bottom === undefined ||
        item.contraction_box_top === undefined
      ) {
        return null;
      }

      const colors = item.major_contraction
        ? COLOR_PRESETS.major
        : COLOR_PRESETS.minor;

      const startTime = timeToTimestamp(item.contraction_box_start);
      const endTime = timeToTimestamp(
        item.contraction_box_end ?? lastCandleTime
      );
      if (startTime == null || endTime == null) return null;

      return new Rectangle(
        chart,
        series,
        {
          time: startTime,
          price: item.contraction_box_bottom
        },
        {
          time: endTime,
          price: item.contraction_box_top
        },
        {
          fillColor: colors.fill,
          borderColor: colors.border,
          borderWidth: 0.5
        }
      );
    },
    []
  );

  const drawIndicatorData = useCallback(
    (
      indicatorIndex: number,
      data: MasterPatternIndicatorUpdate['data'],
      candlestickData: CandlestickUpdatePoint[]
    ) => {
      const chart = chartRef.current;
      const series = candlestickSeriesRef.current;

      if (!chart || !series || candlestickData.length === 0) return;

      const lastCandleTime = candlestickData[candlestickData.length - 1].time;

      // 3) MASTER_PATTERN redraws from scratch on update instead of incremental mutation:
      // rectangles/value lines can move or re-extend, so full rebuild is safer than patching.
      clearPrimitivesForIndicator(indicatorIndex);
      clearValuePointSeriesForIndicator(indicatorIndex);
      clearZigZagSeriesForIndicator(indicatorIndex);

      const uniqueBoxes = getUniqueContractionBoxes(data);
      const newPrimitives: ISeriesPrimitive<Time>[] = [];

      uniqueBoxes.forEach((item) => {
        const rectangle = createRectanglePrimitive(item, lastCandleTime);
        if (!rectangle) return;

        series.attachPrimitive(rectangle);
        newPrimitives.push(rectangle);
      });

      masterPatternRefs.current.set(indicatorIndex, newPrimitives);

      const valuePointItems = uniqueBoxes.filter(
        (item) =>
          item.value_point !== undefined &&
          item.contraction_box_end !== undefined
      );

      const newValuePointSeries: ISeriesApi<'Line'>[] = [];

      valuePointItems.forEach((item) => {
        const startTime = timeToTimestamp(item.contraction_box_end);
        const endTime = timeToTimestamp(
          item.value_point_expired_date ?? lastCandleTime
        );
        if (startTime == null || endTime == null || endTime <= startTime)
          return;

        const continuationStartTime = timeToTimestamp(
          item.value_point_continuation_start
        );

        const addValueLine = (
          fromTime: Time,
          toTime: Time,
          lineStyle: LineStyle
        ) => {
          const valueLine = chart.addSeries(LineSeries, {
            color:
              lineStyle === LineStyle.Solid
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(255, 255, 255, 0.45)',
            lineWidth: 1,
            lineStyle,
            priceLineVisible: false,
            lastValueVisible: false
          });

          valueLine.setData([
            {
              time: fromTime,
              value: item.value_point!
            },
            {
              time: toTime,
              value: item.value_point!
            }
          ]);

          newValuePointSeries.push(valueLine);
        };

        if (continuationStartTime != null && continuationStartTime < endTime) {
          if (continuationStartTime > startTime) {
            addValueLine(startTime, continuationStartTime, LineStyle.Solid);
          }
          addValueLine(
            continuationStartTime > startTime
              ? continuationStartTime
              : startTime,
            endTime,
            LineStyle.SparseDotted
          );
          return;
        }

        addValueLine(startTime, endTime, LineStyle.Solid);
      });

      valuePointSeriesRefs.current.set(indicatorIndex, newValuePointSeries);

      if (!showZigZag) return;

      const zigZagData = data
        .flatMap((item) => item.zigZag ?? [])
        .map((item) => {
          const swingEnd = item.swingEnd;
          const time = timeToTimestamp(swingEnd?.time);
          const value = swingEnd?.price;
          if (time == null || value == null) return null;

          return {
            time,
            value
          };
        })
        .filter(
          (item): item is { time: UTCTimestamp; value: number } => item != null
        );

      if (zigZagData.length === 0) return;

      const zigZagSeries = chart.addSeries(LineSeries, {
        color: 'rgba(255, 255, 255, 0.7)',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });

      zigZagSeries.setData(zigZagData);
      zigZagSeriesRefs.current.set(indicatorIndex, zigZagSeries);
    },
    [
      clearPrimitivesForIndicator,
      clearValuePointSeriesForIndicator,
      clearZigZagSeriesForIndicator,
      createRectanglePrimitive,
      getUniqueContractionBoxes
    ]
  );

  const applyUpdate = useCallback(
    (update: MasterPatternIndicatorUpdate) => {
      // MASTER_PATTERN payload is treated as full indicator state for given index.
      // We replace cached data and redraw, instead of doing point-level merges.
      indicatorDataCacheRef.current.set(update.indicatorIndex, update.data);
      drawIndicatorData(
        update.indicatorIndex,
        update.data,
        getLatestCandlesticks()
      );
    },
    [drawIndicatorData, getLatestCandlesticks]
  );

  useEffect(() => {
    if (!chartInitialized) return;

    // Re-render open-ended visuals only when a new candle is appended.
    const handleNewCandlestick = ({ time }: NewCandlestickEvent) => {
      if (time == null) {
        clearAll();
        return;
      }

      const candlesticks = getLatestCandlesticks();
      if (candlesticks.length === 0) {
        clearAll();
        return;
      }

      // 4) Redraw on candlestick changes even without new indicator payload.
      indicatorDataCacheRef.current.forEach((data, indicatorIndex) => {
        drawIndicatorData(indicatorIndex, data, candlesticks);
      });
    };

    const subscription = newCandlestick$.subscribe(handleNewCandlestick);

    return () => subscription.unsubscribe();
  }, [
    chartInitialized,
    clearAll,
    drawIndicatorData,
    getLatestCandlesticks,
    newCandlestick$,
    timeframeIndex
  ]);

  useEffect(() => {
    if (hasMasterPattern) return;

    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, hasMasterPattern]);

  useEffect(() => {
    // 5) Timeframe switch clears caches and rendered objects to avoid carrying over
    // stale primitives/series from the previously selected timeframe.
    indicatorDataCacheRef.current.clear();
    clearAll();
  }, [clearAll, timeframeIndex]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasMasterPattern,
    indicatorType: IndicatorType.MASTER_PATTERN,
    isExpectedUpdate: isMasterPatternIndicatorUpdate,
    applyUpdate
  });

  useEffect(() => {
    return () => {
      indicatorDataCacheRef.current.clear();
      clearAll();
    };
  }, [clearAll]);
};
